<template>
  <div class="prod-form-page">
    <!-- Hero -->
    <div class="form-hero">
      <p class="form-crumb">Productos / {{ form.id ? 'Editar' : 'Nuevo' }}</p>
      <h1 class="form-title">
        {{ form.id ? 'Editar Producto' : 'Nuevo Producto' }}
      </h1>
      <p class="form-subtitle">
        {{
          form.id
            ? 'Actualiza la información del producto registrado en el sistema.'
            : 'Completa la información del producto para registrarlo en el sistema.'
        }}
      </p>
    </div>

    <div class="form-card">
      <v-form @submit.prevent="submit">
        <!-- ===== Información del Producto ===== -->
        <section class="form-section">
          <h3 class="form-section__title">Información del Producto</h3>
          <div class="form-grid">
            <div class="form-field form-field--full">
              <label class="form-label"
                >Nombre del Producto <span class="form-required">*</span></label
              >
              <v-text-field
                v-model="form.name"
                v-validate="'required'"
                data-vv-name="name"
                :error-messages="errors.collect('name')"
                class="form-input"
                placeholder="Ingrese el nombre del producto"
                solo
                hide-details
              ></v-text-field>
            </div>
            <div class="form-field">
              <label class="form-label"
                >Presentación Comercial
                <span class="form-required">*</span></label
              >
              <v-text-field
                v-model="form.commercialPresentation"
                v-validate="'required'"
                data-vv-name="commercialPresentation"
                :error-messages="errors.collect('commercialPresentation')"
                class="form-input"
                placeholder="Ej: Tabletas, Jarabe, Cápsulas"
                solo
                hide-details
              ></v-text-field>
            </div>
            <div class="form-field">
              <label class="form-label"
                >Registro Sanitario <span class="form-required">*</span></label
              >
              <v-text-field
                v-model="form.sanitaryReg"
                v-validate="'required'"
                data-vv-name="sanitaryReg"
                :error-messages="errors.collect('sanitaryReg')"
                class="form-input"
                placeholder="Ingrese el número de registro sanitario"
                solo
                hide-details
              ></v-text-field>
            </div>
            <div class="form-field">
              <label class="form-label"
                >Expiración de Reg. Sanitario
                <span class="form-required">*</span></label
              >
              <v-dialog
                :return-value.sync="form.sanitaryRegExpiration"
                full-width
                ref="dialog"
                v-model="modalExpdate"
                width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="form.sanitaryRegExpiration"
                    v-validate="'required'"
                    data-vv-name="sanitaryRegExpiration"
                    :error-messages="errors.collect('sanitaryRegExpiration')"
                    class="form-input"
                    placeholder="Seleccione la fecha"
                    append-icon="event"
                    readonly
                    solo
                    hide-details
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  locale="es-es"
                  scrollable
                  v-model="form.sanitaryRegExpiration"
                >
                  <v-spacer></v-spacer>
                  <v-btn flat text color="primary" @click="modalExpdate = false"
                    >Cancelar</v-btn
                  >
                  <v-btn
                    flat
                    text
                    color="primary"
                    @click="$refs.dialog.save(form.sanitaryRegExpiration)"
                    >OK</v-btn
                  >
                </v-date-picker>
              </v-dialog>
            </div>
          </div>
        </section>

        <div class="form-divider"></div>

        <!-- ===== Clasificación ===== -->
        <section class="form-section">
          <h3 class="form-section__title">Clasificación</h3>
          <div class="form-grid">
            <div class="form-field">
              <label class="form-label"
                >Laboratorio <span class="form-required">*</span></label
              >
              <v-select
                v-model="form.laboratory"
                v-validate="'required'"
                data-vv-name="laboratory"
                :error-messages="errors.collect('laboratory')"
                :items="laboratories"
                item-text="text"
                item-value="value"
                class="form-input"
                :placeholder="
                  form.laboratory ? '' : 'Seleccione un laboratorio'
                "
                no-data-text="Datos no disponibles"
                solo
                hide-details
              ></v-select>
            </div>
            <div class="form-field">
              <label class="form-label">
                Órgano Anuente
                <span v-if="!isEditing" class="form-required">*</span>
                <span v-else class="form-label-opt">(opcional)</span>
              </label>
              <v-select
                v-model="form.organoAnuenteId"
                v-validate="isEditing ? '' : 'required'"
                data-vv-name="organoAnuenteId"
                :error-messages="errors.collect('organoAnuenteId')"
                :items="organosAnuentes"
                item-text="text"
                item-value="value"
                class="form-input"
                :placeholder="
                  form.organoAnuenteId ? '' : 'Seleccione un órgano anuente'
                "
                no-data-text="Datos no disponibles"
                clearable
                solo
                hide-details
              ></v-select>
            </div>
          </div>
        </section>

        <div class="form-divider"></div>

        <!-- ===== Documentación ===== -->
        <section class="form-section">
          <h3 class="form-section__title">Documentación</h3>
          <p class="form-section__help">
            Puede adjuntar el archivo del registro sanitario en formato PDF, JPG
            o PNG.
          </p>
          <div class="form-grid">
            <div class="form-field form-field--full">
              <label class="form-label">
                Archivo de Registro Sanitario
                <span class="form-label-opt">(opcional)</span>
              </label>
              <div
                class="file-input-wrap"
                :class="{
                  'file-input-wrap--error': errors.collect('sanitaryRegFile')
                    .length,
                }"
              >
                <v-icon small class="file-input-wrap__icon">attach_file</v-icon>
                <span
                  class="file-input-wrap__name"
                  :class="{
                    'file-input-wrap__name--selected': form.sanitaryRegFile,
                  }"
                  >{{
                    form.sanitaryRegFile
                      ? form.sanitaryRegFile.name
                      : 'Seleccione un archivo'
                  }}</span
                >
                <span
                  v-if="form.sanitaryRegFile"
                  class="file-input-wrap__size"
                  >{{ formatFileSize(form.sanitaryRegFile.size) }}</span
                >
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  class="file-input-wrap__native"
                  @change="onFileChange"
                />
              </div>
              <p
                v-if="errors.collect('sanitaryRegFile').length"
                class="form-error-text"
              >
                {{ errors.collect('sanitaryRegFile')[0] }}
              </p>
              <p class="form-hint">
                Formatos aceptados: PDF, JPG, PNG. Tamaño máximo recomendado:
                5MB.
              </p>
              <div class="file-preview-actions">
                <v-btn
                  v-if="isEditing"
                  class="btn-secondary"
                  small
                  :loading="previewLoading"
                  @click="openExistingFilePreview"
                >
                  <v-icon left small>visibility</v-icon>Ver archivo del producto
                </v-btn>
                <v-btn
                  v-if="selectedSanitaryRegFilePreviewUrl"
                  class="btn-secondary"
                  small
                  @click="openSelectedFilePreview"
                >
                  <v-icon left small>preview</v-icon>Ver archivo seleccionado
                </v-btn>
              </div>
            </div>
          </div>
        </section>

        <!-- ===== Footer ===== -->
        <div class="form-footer">
          <v-btn class="btn-cancel" flat @click="onCancel">
            <v-icon left small>close</v-icon>Cancelar
          </v-btn>
          <v-btn type="submit" class="btn-save" :loading="saving">
            <v-icon left small>save</v-icon>
            {{ form.id ? 'Guardar Cambios' : 'Guardar Producto' }}
          </v-btn>
        </div>
      </v-form>
    </div>

    <v-dialog max-width="500" v-if="dialog.message" v-model="dialog.show">
      <message
        :key="dialog.key"
        :options="dialog"
        v-on:close="dialog.show = false"
      ></message>
    </v-dialog>

    <v-dialog
      v-model="filePreviewDialog.show"
      max-width="920"
      @input="
        value => {
          if (!value) closeFilePreview()
        }
      "
    >
      <v-card class="modal-card">
        <div class="modal-header">
          <h3 class="modal-header__title">
            {{ filePreviewDialog.title || 'Vista previa de archivo' }}
          </h3>
        </div>
        <v-card-text class="modal-body modal-body--preview">
          <img
            v-if="isImagePreview(filePreviewDialog)"
            :src="filePreviewDialog.url"
            alt="Vista previa"
            class="file-preview-image"
          />
          <iframe
            v-else-if="isPdfPreview(filePreviewDialog)"
            :src="filePreviewDialog.url"
            class="file-preview-pdf"
            title="Vista previa PDF"
          ></iframe>
          <div v-else class="file-preview-fallback">
            <v-icon class="file-preview-fallback__icon"
              >insert_drive_file</v-icon
            >
            <p>No se pudo previsualizar este archivo.</p>
            <a :href="filePreviewDialog.url" target="_blank" rel="noopener"
              >Abrir archivo en una nueva pestaña</a
            >
          </div>
        </v-card-text>
        <div class="modal-footer">
          <v-btn class="btn-cancel" flat small @click="closeFilePreview"
            >Cerrar</v-btn
          >
          <v-btn
            class="btn-save"
            small
            :href="filePreviewDialog.url"
            target="_blank"
            rel="noopener"
            >Abrir en nueva pestaña</v-btn
          >
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Api from '@/api/index'
import message from './../dialog/Message'
export default {
  components: {
    message,
  },
  props: {
    form: {
      type: Object,
      default() {
        return {
          name: '',
          commercialPresentation: '',
          sanitaryReg: '',
          sanitaryRegExpiration: null,
          laboratory: '',
          organoAnuenteId: '',
          sanitaryRegFile: null,
        }
      },
    },
    productId: {
      type: [Number, String],
      default: null,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      saving: false,
      modalExpdate: false,
      laboratories: [],
      organosAnuentes: [],
      existingSanitaryRegFile: '',
      selectedSanitaryRegFilePreviewUrl: '',
      previewLoading: false,
      existingPreviewObjectUrl: '',
      filePreviewDialog: {
        show: false,
        title: '',
        url: '',
        contentType: '',
      },
      dialog: {
        show: false,
        key: 0,
      },
    }
  },

  computed: {
    effectiveId() {
      if (this.productId != null && this.productId !== '') return this.productId
      if (this.$route && this.$route.params && this.$route.params.id)
        return this.$route.params.id
      return null
    },
    isEditing() {
      return this.effectiveId != null
    },
  },

  created() {
    this.$store.dispatch('laboratories/getAll', {}).then(response => {
      const data = (response && response.items) || []
      for (let index = 0; index < data.length; index += 1) {
        this.laboratories.push({
          text: data[index].name,
          value: Number(data[index].id),
        })
      }
    })

    Api.OrganoAnuente.getBasicList().then(response => {
      let list = []
      if (Array.isArray(response)) list = response
      else if (response && Array.isArray(response.items)) list = response.items
      else if (
        response &&
        response.data &&
        Array.isArray(response.data.items)
      ) {
        list = response.data.items
      } else if (response && response.data && Array.isArray(response.data)) {
        list = response.data
      }

      this.organosAnuentes = list.map(item => ({
        text: item.name,
        value: Number(item.id),
      }))
    })

    if (this.effectiveId != null) {
      this.$store
        .dispatch('products/getById', {
          id: this.effectiveId,
        })
        .then(response => {
          if (response) {
            const rawOrganoId =
              response.organoAnuenteId ||
              (response.organoAnuente && response.organoAnuente.id) ||
              (response.product && response.product.organoAnuenteId) ||
              (response.product &&
                response.product.organoAnuente &&
                response.product.organoAnuente.id)

            const organoIdNum =
              rawOrganoId != null && rawOrganoId !== ''
                ? Number(rawOrganoId)
                : null
            const labRaw =
              (response.laboratory && response.laboratory.id) ||
              response.laboratory
            const labIdNum =
              labRaw != null && labRaw !== '' ? Number(labRaw) : null

            this.form = Object.assign(this.form, {
              id: response.id,
              name:
                (response.product && response.product.name) ||
                response.name ||
                '',
              commercialPresentation:
                response.commercialPresentation || response.fxFormula || '',
              sanitaryReg: response.sanitaryReg,
              sanitaryRegExpiration:
                response.sanitaryRegExpiration || response.expirationDate,
              laboratory: Number.isFinite(labIdNum) ? labIdNum : labRaw,
              organoAnuenteId: Number.isFinite(organoIdNum)
                ? organoIdNum
                : null,
              sanitaryRegFile: null,
            })
            this.existingSanitaryRegFile =
              (response.product && response.product.sanitaryRegFile) ||
              response.sanitaryRegFile ||
              ''
          } else if (!this.embedded) {
            this.$router.push('/productos/listado')
          }
        })
    }
  },
  beforeDestroy() {
    if (this.selectedSanitaryRegFilePreviewUrl) {
      URL.revokeObjectURL(this.selectedSanitaryRegFilePreviewUrl)
    }
    this.clearExistingPreviewUrl()
  },
  methods: {
    onFileChange(event) {
      if (this.selectedSanitaryRegFilePreviewUrl) {
        URL.revokeObjectURL(this.selectedSanitaryRegFilePreviewUrl)
        this.selectedSanitaryRegFilePreviewUrl = ''
      }

      this.form.sanitaryRegFile = event.target.files[0] || null
      if (this.form.sanitaryRegFile instanceof File) {
        this.selectedSanitaryRegFilePreviewUrl = URL.createObjectURL(
          this.form.sanitaryRegFile
        )
      }
      this.$validator.errors.remove('sanitaryRegFile')
    },

    getFileName(path) {
      if (!path) return ''
      return path.split('/').pop()
    },

    getContentType(headers) {
      if (!headers) return ''
      return headers['content-type'] || headers['Content-Type'] || ''
    },

    getFileNameFromHeaders(headers) {
      if (!headers) return ''
      const contentDisposition =
        headers['content-disposition'] || headers['Content-Disposition'] || ''
      const match = /filename\*=UTF-8''([^;]+)|filename=\"?([^\";]+)\"?/i.exec(
        contentDisposition
      )
      const encodedName = match && (match[1] || match[2])
      return encodedName ? decodeURIComponent(encodedName) : ''
    },

    isImagePreview(preview) {
      const mime = (preview && preview.contentType
        ? preview.contentType
        : ''
      ).toLowerCase()
      if (mime.indexOf('image/') === 0) return true
      const title = (preview && preview.title
        ? preview.title
        : ''
      ).toLowerCase()
      return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(title)
    },

    isPdfPreview(preview) {
      const mime = (preview && preview.contentType
        ? preview.contentType
        : ''
      ).toLowerCase()
      if (mime.indexOf('application/pdf') === 0) return true
      const title = (preview && preview.title
        ? preview.title
        : ''
      ).toLowerCase()
      return /\.pdf$/.test(title)
    },

    clearExistingPreviewUrl() {
      if (this.existingPreviewObjectUrl) {
        URL.revokeObjectURL(this.existingPreviewObjectUrl)
        this.existingPreviewObjectUrl = ''
      }
    },

    closeFilePreview() {
      this.filePreviewDialog.show = false
      this.clearExistingPreviewUrl()
    },

    async openExistingFilePreview() {
      if (!this.form.id) return
      this.previewLoading = true
      this.clearExistingPreviewUrl()
      try {
        const response = await Api.Product.getDocumentByProduct(this.form.id)
        if (!response || response.status !== 200 || !response.data) {
          throw new Error('No se pudo obtener el documento del producto.')
        }

        const contentType = this.getContentType(response.headers)
        const fileNameFromHeaders = this.getFileNameFromHeaders(
          response.headers
        )
        const fallbackName = this.getFileName(this.existingSanitaryRegFile)
        const blob =
          response.data instanceof Blob
            ? response.data
            : new Blob([response.data], {
                type: contentType || 'application/octet-stream',
              })

        this.existingPreviewObjectUrl = URL.createObjectURL(blob)
        this.filePreviewDialog = {
          show: true,
          title:
            fileNameFromHeaders || fallbackName || `producto-${this.form.id}`,
          url: this.existingPreviewObjectUrl,
          contentType: blob.type || contentType || '',
        }
      } catch (error) {
        this.dialog = {
          message: 'No se pudo cargar el archivo del producto.',
          color: 'error',
          show: true,
          key: this.dialog.key + 1,
        }
      } finally {
        this.previewLoading = false
      }
    },

    openSelectedFilePreview() {
      if (!this.selectedSanitaryRegFilePreviewUrl || !this.form.sanitaryRegFile)
        return
      this.filePreviewDialog = {
        show: true,
        title: this.form.sanitaryRegFile.name || 'Archivo seleccionado',
        url: this.selectedSanitaryRegFilePreviewUrl,
        contentType: this.form.sanitaryRegFile.type || '',
      }
    },

    formatFileSize(bytes) {
      if (!bytes) return ''
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    },

    onCancel() {
      if (this.embedded) {
        this.$emit('cancel')
      } else {
        this.$router.push('/productos/listado')
      }
    },
    submit() {
      this.$validator.validateAll().then(result => {
        if (result) {
          this.saving = true
          const payload = {
            id: this.form.id,
            name: this.form.name,
            commercialPresentation: this.form.commercialPresentation,
            sanitaryReg: this.form.sanitaryReg,
            sanitaryRegExpiration: this.form.sanitaryRegExpiration,
            laboratory: this.form.laboratory,
            organoAnuenteId: this.form.organoAnuenteId,
          }
          if (this.form.sanitaryRegFile instanceof File) {
            payload.sanitaryRegFile = this.form.sanitaryRegFile
          }

          this.$store
            .dispatch('products/save', payload)
            .then(response => {
              this.saving = false
              if (response.status === 200) {
                if (this.embedded) {
                  this.$emit('saved', response)
                  return
                }
                if (this.form.id) {
                  this.dialog = {
                    message: 'Los datos fueron actualizados exitosamente',
                    color: 'primary',
                    show: true,
                    key: this.dialog.key + 1,
                  }
                } else {
                  this.$router.push('/productos/listado?success=true')
                }
              } else {
                this.dialog = {
                  message: response.data.error,
                  color: 'error',
                  show: true,
                  key: this.dialog.key + 1,
                }
              }
            })
            .catch(() => {
              this.saving = false
              this.dialog = {
                message: 'No se pudo conectar al servidor. Intenta más tarde.',
                color: 'error',
                show: true,
                key: this.dialog.key + 1,
              }
            })
        }
      })
    },
  },
}
</script>

<style scoped>
.prod-form-page {
  padding: 24px 28px 40px;
  color: #172133;
  background: #f9fafb;
  min-height: 100%;
}

.form-hero {
  margin-bottom: 24px;
}

.form-crumb {
  margin: 0 0 6px;
  font-size: 12.5px;
  color: #94a3b8;
  letter-spacing: 0.02em;
}

.form-title {
  margin: 0 0 6px;
  font-size: 26px;
  font-weight: 700;
  color: #172133;
  letter-spacing: -0.01em;
}

.form-subtitle {
  margin: 0;
  font-size: 13.5px;
  color: #64748b;
  max-width: 720px;
}

.form-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 32px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04),
    0 4px 14px rgba(15, 23, 42, 0.05);
}

.form-section {
  margin-bottom: 0;
}

.form-section__title {
  margin: 0 0 6px;
  font-size: 14px;
  font-weight: 700;
  color: #172133;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  position: relative;
  padding-bottom: 10px;
}

.form-section__title::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 32px;
  height: 2px;
  background: #e5252a;
  border-radius: 1px;
}

.form-section__badge {
  font-size: 11px;
  font-weight: 600;
  color: #254f87;
  text-transform: none;
  letter-spacing: 0;
  margin-left: 6px;
}

.form-section__help {
  margin: 0 0 16px;
  font-size: 12.5px;
  color: #64748b;
}

.form-divider {
  height: 1px;
  background: #eef2f7;
  margin: 24px 0;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 18px;
  margin-top: 16px;
}

.form-field--full {
  grid-column: 1 / -1;
}

.form-field {
  display: flex;
  flex-direction: column;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
  letter-spacing: 0.01em;
}

.form-required {
  color: #e5252a;
  font-weight: 700;
}

.form-label-opt {
  font-size: 11px;
  font-weight: 400;
  color: #94a3b8;
  margin-left: 2px;
}

.form-hint {
  margin: 6px 0 0;
  font-size: 11.5px;
  color: #94a3b8;
}

.form-error-text {
  margin: 6px 2px 0;
  color: #e5252a;
  font-size: 11.5px;
  font-weight: 600;
}

.file-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.15s ease;
  overflow: hidden;
}

.file-input-wrap:hover {
  border-color: #cbd5e1;
}

.file-input-wrap--error {
  border-color: #e5252a !important;
  box-shadow: 0 0 0 3px rgba(229, 37, 42, 0.08);
}

.file-input-wrap__icon {
  color: #94a3b8 !important;
  flex-shrink: 0;
}

.file-input-wrap__name {
  flex: 1;
  font-size: 14px;
  color: #94a3b8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-input-wrap__name--selected {
  color: #172133;
}

.file-input-wrap__size {
  font-size: 11.5px;
  color: #94a3b8;
  flex-shrink: 0;
}

.file-input-wrap__native {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
  width: 100%;
  height: 100%;
}

.form-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid #eef2f7;
}

.btn-cancel {
  border: 1px solid #e2e8f0 !important;
  background: transparent !important;
  color: #475569 !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  height: 42px !important;
  padding: 0 18px !important;
  box-shadow: none !important;
}

.btn-cancel:hover {
  background: #f8fafc !important;
  color: #172133 !important;
  border-color: #cbd5e1 !important;
}

.btn-save {
  background: #e5252a !important;
  color: #ffffff !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  height: 42px !important;
  padding: 0 22px !important;
  box-shadow: 0 1px 2px rgba(229, 37, 42, 0.12),
    0 4px 12px rgba(229, 37, 42, 0.2) !important;
  transition: all 0.15s ease !important;
}

.btn-save:hover {
  background: #c91e23 !important;
  box-shadow: 0 2px 4px rgba(229, 37, 42, 0.18),
    0 6px 16px rgba(229, 37, 42, 0.28) !important;
  transform: translateY(-1px);
}

.btn-secondary {
  border: 1px solid #e2e8f0 !important;
  background: #ffffff !important;
  color: #254f87 !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  box-shadow: none !important;
}

.btn-secondary:hover {
  background: #f8fafc !important;
  border-color: #cbd5e1 !important;
}

.file-preview-actions {
  margin-top: 10px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.modal-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.modal-header {
  padding: 18px 20px 14px;
  border-bottom: 1px solid #eef2f7;
}

.modal-header__title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #172133;
}

.modal-body {
  padding: 14px 20px !important;
}

.modal-body--preview {
  min-height: 320px;
}

.file-preview-image {
  display: block;
  max-width: 100%;
  max-height: 70vh;
  margin: 0 auto;
  border-radius: 8px;
}

.file-preview-pdf {
  width: 100%;
  height: 70vh;
  border: 0;
  border-radius: 8px;
  background: #f8fafc;
}

.file-preview-fallback {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #64748b;
}

.file-preview-fallback__icon {
  font-size: 42px !important;
  color: #94a3b8 !important;
}

@media (max-width: 720px) {
  .prod-form-page {
    padding: 18px 14px 28px;
  }
  .form-card {
    padding: 18px 16px;
  }
  .form-title {
    font-size: 22px;
  }
  .form-grid {
    grid-template-columns: 1fr;
  }
  .form-footer {
    flex-direction: column-reverse;
  }
  .btn-cancel,
  .btn-save {
    width: 100%;
    justify-content: center;
  }
  .file-preview-pdf {
    height: 60vh;
  }
}
</style>

<style>
.prod-form-page
  .form-input.v-text-field.v-text-field--solo
  .v-input__control
  > .v-input__slot,
.prod-form-page
  .form-input.v-select.v-text-field--solo
  .v-input__control
  > .v-input__slot {
  background: #ffffff !important;
  border: 1px solid #d1d5db !important;
  border-radius: 10px !important;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04) !important;
  min-height: 44px !important;
  padding: 0 14px !important;
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    background 0.18s ease;
}

.prod-form-page
  .form-input.v-text-field.v-text-field--solo:hover
  .v-input__control
  > .v-input__slot,
.prod-form-page
  .form-input.v-select.v-text-field--solo:hover
  .v-input__control
  > .v-input__slot {
  border-color: #9ca3af !important;
}

.prod-form-page
  .form-input.v-text-field.v-text-field--solo.v-input--is-focused
  .v-input__control
  > .v-input__slot,
.prod-form-page
  .form-input.v-select.v-text-field--solo.v-input--is-focused
  .v-input__control
  > .v-input__slot {
  border-color: #254f87 !important;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.12),
    0 1px 2px rgba(15, 23, 42, 0.04) !important;
}

.prod-form-page .form-input.error--text .v-input__control > .v-input__slot {
  border-color: #ef4444 !important;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12) !important;
}

.prod-form-page .form-input input,
.prod-form-page .form-input textarea {
  color: #172133 !important;
  font-size: 14px !important;
  caret-color: #172133 !important;
}

.prod-form-page .form-input input::placeholder,
.prod-form-page .form-input textarea::placeholder {
  color: #94a3b8 !important;
  opacity: 1;
}

.prod-form-page .form-input .v-input__icon .v-icon {
  color: #94a3b8 !important;
  font-size: 19px !important;
}
</style>
